// The simple Email QA injector belongs to one authorized session/role/business at a time.
// UI availability is convenience; the server independently enforces role and tenant capability.
import React from "react";
import { Button, Card, FormField, Select, StatusPill } from "@/ds";
import { cockpit, getTenantContext } from "@/api";
import type { TestMailCatalog, TestMailInjectResult } from "@/api";
import { Icon } from "@/lib";
import { useLang, useT } from "../i18n";
import { useSession } from "../session";
import type { PushToast } from "@/notifications/toast-queue";
import { testMailContextKey } from "./test-mail-access";

export interface TestMailPanelProps {
  pushToast: PushToast;
  /** Reload the list so the injected request appears without a manual refresh. */
  onInjected?: (requestId: string) => void;
  /**
   * Open one request. #767: the list is one row per CONVERSATION, and a conversation is keyed by
   * the sender address — so injecting a scenario a SECOND time joins the first one's conversation
   * and the list gains no row. A reload alone therefore looks like nothing happened, which is
   * exactly what was reported. The panel keeps the operator here (injecting several in a row is a
   * normal thing to do) and offers a direct way to the request it just made.
   *
   * Required, not optional: a caller that forgets it would silently drop the way back, which is
   * the whole defect. A compile error is the right way to find that out.
   */
  onOpenRequest: (requestId: string) => void;
}

export function TestMailPanel(props: TestMailPanelProps) {
  const session = useSession();
  const contextKey = testMailContextKey(session);
  const currentKey = React.useRef(contextKey);
  currentKey.current = contextKey;
  if (!contextKey) return null;
  // A keyed child drops the entire catalog, selection, result and busy state before another
  // context can render. The API carrier changes synchronously at the start of a tenant switch.
  return <TestMailForm key={contextKey} {...props}
    isCurrentContext={() => currentKey.current === contextKey && getTenantContext() === session.tenantContext} />;
}

function TestMailForm({ pushToast, onInjected, onOpenRequest, isCurrentContext }: TestMailPanelProps & {
  isCurrentContext: () => boolean;
}) {
  const t = useT();
  const { lang } = useLang();

  const [lastInjected, setLastInjected] = React.useState<TestMailInjectResult | null>(null);
  const [catalog, setCatalog] = React.useState<TestMailCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = React.useState(true);
  const [category, setCategory] = React.useState("");
  const [scenario, setScenario] = React.useState("");
  const [busy, setBusy] = React.useState<"pick" | "random" | null>(null);
  const [open, setOpen] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const mounted = React.useRef(false);
  React.useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const current = () => mounted.current && isCurrentContext();

  React.useEffect(() => {
    if (!current()) return;
    let cancelled = false;
    setCatalog(null);
    setCatalogLoading(true);
    cockpit
      .testMailCatalog()
      .then((next) => {
        if (!cancelled && current()) {
          setCatalog(next);
          setCatalogLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled && current()) {
          setCatalog(null);
          setCatalogLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [reload]);

  const label = (option: { label_de: string; label_en: string }) =>
    lang === "en" ? option.label_en : option.label_de;
  const expectation = (option: { expectation_de: string; expectation_en: string }) =>
    lang === "en" ? option.expectation_en : option.expectation_de;

  const visible = React.useMemo(
    () => (catalog?.scenarios ?? []).filter((s) => !category || s.category === category),
    [catalog, category],
  );
  // Narrowing the category must never leave the Select showing a value it no longer offers.
  React.useEffect(() => {
    if (!visible.length) return;
    if (!visible.some((s) => s.key === scenario)) setScenario(visible[0].key);
  }, [visible, scenario]);
  const selected = visible.find((s) => s.key === scenario) ?? null;

  const announce = (injected: TestMailInjectResult) => {
    pushToast({
      tone: "success",
      title: t("testMail.done"),
      msg: t("testMail.doneBody", {
        scenario: label(injected.scenario),
        outcome: expectation(injected.scenario),
      }),
    });
    setScenario(injected.scenario.key);
    setLastInjected(injected);
    onInjected?.(injected.request_id);
  };

  const run = async (mode: "pick" | "random") => {
    if (!current() || busy || !catalog?.mailbox_address || !visible.length
        || (mode === "pick" && !selected)) return;
    setBusy(mode);
    try {
      const result = mode === "pick"
        ? await cockpit.testMailInject(scenario)
        : await cockpit.testMailInjectRandom(category || undefined);
      if (current()) announce(result);
    } catch (e) {
      if (current()) pushToast({ tone: "danger", title: t("common.error"), msg: (e as Error)?.message || "" });
    } finally {
      if (current()) setBusy(null);
    }
  };

  return (
    <section
      className="test-mail-panel"
      data-testid="test-mail-panel"
      aria-busy={catalogLoading}
    >
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<Icon name={open ? "chevronDown" : "chevronRight"} size={16} />}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setOpen(!open);
          if (open) event?.currentTarget.focus();
        }}
        aria-expanded={open}
        aria-controls={open ? "test-mail-form" : undefined}
        disabled={!catalog}
        data-testid="test-mail-toggle"
      >
        {t("testMail.title")}
        {!catalog && ` · ${t(catalogLoading ? "common.loading" : "common.error")}`}
      </Button>
      {!catalogLoading && !catalog && <div role="alert" className="stack-sm">
        <p>{t("testMail.catalogError")}</p>
        <Button variant="secondary" size="sm" data-testid="test-mail-retry"
          onClick={() => { if (current()) setReload((value) => value + 1); }}>{t("common.retry")}</Button>
      </div>}
      {open && catalog && (
        <Card id="test-mail-form" className="test-mail-panel__form">
          <div className="stack-sm">
            <div className="row-inline" style={{ gap: "var(--space-2)" }}>
              <StatusPill tone="warning" variant="soft" size="sm" label={t("testMail.badge")} />
            </div>
            <p className="subtle" style={{ margin: 0 }}>
              {catalog.mailbox_address
                ? t("testMail.hint", { mailbox: catalog.mailbox_address })
                : t("testMail.noMailbox")}
            </p>
            <FormField label={t("testMail.category")}>
              <Select
                size="sm"
                value={category}
                options={[
                  { value: "", label: t("testMail.categoryAll") },
                  ...catalog.categories.map((c) => ({ value: c.id, label: label(c) })),
                ]}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(e.target.value)}
                data-testid="test-mail-category"
              />
            </FormField>
            <FormField label={t("testMail.scenario")}>
              <Select
                size="sm"
                value={scenario}
                options={visible.map((s) => ({ value: s.key, label: label(s) }))}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setScenario(e.target.value)}
                data-testid="test-mail-scenario"
              />
            </FormField>
            {selected && (
              <p className="subtle" style={{ margin: 0 }} data-testid="test-mail-expectation">
                {t("testMail.expectation")}: {expectation(selected)}
              </p>
            )}
            <div className="row-inline" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
              <Button loading={busy === "pick"}
                variant="primary"
                size="sm"
                disabled={busy !== null || !scenario || !catalog.mailbox_address}
                onClick={() => void run("pick")}
                data-testid="test-mail-send"
              >
                {t("testMail.send")}
              </Button>
              <Button loading={busy === "random"}
                variant="secondary"
                size="sm"
                iconLeft={<Icon name="sparkles" size={16} />}
                disabled={busy !== null || !visible.length || !catalog.mailbox_address}
                onClick={() => void run("random")}
                data-testid="test-mail-random"
              >
                {t("testMail.random")}
              </Button>
            </div>
            {lastInjected && (
              <p className="subtle" style={{ margin: 0 }} data-testid="test-mail-last">
                {t("testMail.lastInjected", { scenario: label(lastInjected.scenario) })}{" "}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { if (current()) onOpenRequest(lastInjected.request_id); }}
                  data-testid="test-mail-open-last"
                >
                  {t("testMail.openLast")}
                </Button>
              </p>
            )}
            {/* The senders live in reserved `.example` domains, so an approved reply fails at
                SMTP. That is wanted — but the operator has to be told why. */}
            <p className="subtle" style={{ margin: 0 }}>{t("testMail.replyWarning")}</p>
          </div>
        </Card>
      )}
    </section>
  );
}
